import { CdkScrollable } from '@angular/cdk/scrolling';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
  input,
  output,
  runInInjectionContext,
  Signal,
  viewChild,
} from '@angular/core';

import { provide } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import {
  injectTellentIsOneSettingsEnabled,
  provideTellentNavigationController,
  provideTellentNavigationItems,
  TellentNavigationDataFacade,
  TellentNavigationRoutingService,
  TellentNavigationViewModelBuilder,
  TellentSettingsNavigationController,
} from '@core/tellent-logic';
import {
  BRAND,
  injectTellentSettingsLeaveUrl,
  TellentBrand,
  TellentClientHomepageID,
  TellentNavigationItem,
} from '@core/tellent-types';
import { toLazySignal } from '@core/tellent-util';

import { TellentAppSwitcherNextComponent } from '../../tellent-app-switcher-next/tellent-app-switcher-next.component';
import { TellentNavigationLinkComponent } from '../navigation-link/navigation-link.component';
import { TellentNavigationComponent } from '../navigation.component';

@Component({
  selector: 'tlc-settings-navigation',
  styleUrls: ['settings-navigation.component.less', 'settings-navigation.component.desktop.less'],
  templateUrl: 'settings-navigation.component.html',
  imports: [
    TellentNavigationComponent,
    TellentAppSwitcherNextComponent,
    TellentNavigationLinkComponent,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [
    provide(BRAND, {
      useFactory: () =>
        injectTellentIsOneSettingsEnabled() ? TellentBrand.Tellent : inject(BRAND),
    }),
    provideTellentNavigationItems({
      useFactory: () =>
        <Signal<TellentNavigationItem[]>>(
          toLazySignal(inject(TellentNavigationDataFacade).navigationSettings$, {
            initialValue: [],
          })
        ),
    }),
    provideTellentNavigationController({ useClass: TellentSettingsNavigationController }),
    TellentNavigationViewModelBuilder,
  ],
  hostDirectives: [CdkScrollable],
})
export class TellentSettingsNavigationComponent {
  #injector = inject(Injector);
  #routingSvc = inject(TellentNavigationRoutingService);
  #settingsLeaveUrl = injectTellentSettingsLeaveUrl({ optional: true });

  public appSwitcher = viewChild(TellentAppSwitcherNextComponent);

  public walkthroughId = input<string>();
  public appSwitcherWidthChange = output<number>();

  public ngAfterViewInit() {
    this.emitOffsetChange();
  }

  public goToHome(event: MouseEvent) {
    if (!event.metaKey) event.preventDefault();

    if (this.#settingsLeaveUrl) {
      return this.#routingSvc.navigate(this.#settingsLeaveUrl);
    }

    const route = this.#routingSvc
      .recognizedRoutes()
      .find(route => route.id === TellentClientHomepageID);

    if (route) {
      this.#routingSvc.navigate(route?.buildPath());
    } else {
      console.warn(
        `[TellentClient] No homepage route recognized, provide it with tellentHomeRouteFactory`,
      );
    }
  }

  private emitOffsetChange() {
    runInInjectionContext(this.#injector, () => {
      afterNextRender(() => {
        const bbox = this.appSwitcher()?.triggerEl()?.nativeElement.getBoundingClientRect();

        this.appSwitcherWidthChange.emit(bbox.right);
      });
    });
  }
}
